/**
 * Applies a school-scoped CSS theme after authentication.
 *
 * Schools are identified from the logged-in learning period
 * (`learningPeriod.school_path.school`) using `badge_logo`, `name`, or
 * `saml_entity`. Only aivancity is themed today.
 *
 * The TypeScript hook exists solely to set or clear `data-school-theme` on
 * `<html>` so CSS custom properties can restyle existing chrome. It does not
 * change copy, layout, routing, or API behavior. The attribute is removed on
 * logout and is never set before login, so pre-auth screens stay default.
 */

export const SCHOOL_THEME_ATTR = 'data-school-theme';
export const AIVANCITY_THEME_KEY = 'aivancity';

export interface SchoolThemeSource {
    name?: string;
    badge_logo?: string;
    saml_entity?: string;
}

export class SchoolThemeHelper {

    static resolveThemeKey(school?: SchoolThemeSource | null): string | null {
        if (!school) {
            return null;
        }

        const tokens = [school.badge_logo, school.name, school.saml_entity]
            .filter((value): value is string => typeof value === 'string' && value.length > 0)
            .map(value => SchoolThemeHelper.normalize(value));

        if (tokens.some(token => token === AIVANCITY_THEME_KEY || token.includes(AIVANCITY_THEME_KEY))) {
            return AIVANCITY_THEME_KEY;
        }

        return null;
    }

    static applyFromSchool(school?: SchoolThemeSource | null): void {
        if (typeof document === 'undefined' || !document.documentElement) {
            return;
        }

        const key = SchoolThemeHelper.resolveThemeKey(school);
        if (key) {
            document.documentElement.setAttribute(SCHOOL_THEME_ATTR, key);
        } else {
            document.documentElement.removeAttribute(SCHOOL_THEME_ATTR);
        }
    }

    static applyFromAuthData(authData?: {
        data?: {
            learningPeriod?: {
                school_path?: {
                    school?: SchoolThemeSource;
                };
            };
        };
    } | null): void {
        SchoolThemeHelper.applyFromSchool(authData?.data?.learningPeriod?.school_path?.school ?? null);
    }

    private static normalize(value: string): string {
        return value.toLowerCase().replace(/[^a-z0-9]+/g, '');
    }

}
